import { useRouter } from 'next/router';
import useSWR from 'swr/immutable';
import { removeHost } from '@packages/shared';

import { useClientSideRestParams } from '../utils/restParams';
import { API_URL } from '../api/rest/endpoints';
import { searchFetcher } from '../api/rest/fetchers';
import type { SearchApiRequestOptions } from '../api/rest/types';
import { decodeSearchQuery } from '../queryEncoding';
import { useCleanCategoryPathname } from './useCleanCategoryPathname';

export const useSearchResult = ({
  pageNumber,
  referrer,
}: {
  /**
   * For which page (with current paging mode and page size) to fetch the data, 1-based
   */
  pageNumber?: number;
  referrer?: string;
}) => {
  const { pathname, query } = useRouter();

  const searchquery = decodeSearchQuery(query);

  const sharedOptions = useClientSideRestParams(referrer, pageNumber);

  const cleanCategoryPathname = useCleanCategoryPathname();

  const options: SearchApiRequestOptions = {
    ...sharedOptions,
    ...(pathname.startsWith('/s/') && searchquery
      ? { query: searchquery, category: typeof query.c === 'string' ? query.c : undefined }
      : { uri: cleanCategoryPathname }),
  };

  const cacheKey =
    pageNumber && pageNumber > 1 ? [removeHost(API_URL), options] : 'search-api-result';

  const { data, error, isLoading } = useSWR(cacheKey, () =>
    searchFetcher([removeHost(API_URL), options]),
  );

  // in suspense mode, data should always be defined
  return {
    response: data,
    isLoading,
    error,
    options,
  };
};
